import { type ComponentType } from 'react';
import { Button, Card, CardContent } from '@tark/shared/components';
import { ArrowRight, BookOpenCheck, Calendar, CheckCircle2, KeyRound, Users } from 'lucide-react';
import type { CoachStudioSetupPanelProps } from './coachStudioTypes';

export default function CoachStudioSetupPanel({
  linkedAthleteCount,
  activeJoinCodeCount,
  pendingInvitationCount,
  programCount,
  onOpenCatalog,
  onOpenDirectory,
  onOpenAccess,
  onOpenWork,
}: CoachStudioSetupPanelProps) {
  const hasCatalog = programCount > 0;
  const hasStudents = linkedAthleteCount > 0;
  const hasAccess = activeJoinCodeCount > 0 || pendingInvitationCount > 0;

  return (
    <Card variant="default" padding="none" className="overflow-hidden border-primary/15">
      <CardContent className="p-5 md:p-6">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">First setup</p>
            <h2 className="mt-2 text-2xl font-semibold tracking-tight text-text">Build your coaching workspace</h2>
            <p className="mt-2 max-w-xl text-sm font-medium text-text-muted">
              Create classes, open family access, then schedule the first session.
            </p>
          </div>

          <Button onClick={hasCatalog ? onOpenWork : onOpenCatalog} rightIcon={<ArrowRight className="h-4 w-4" />}>
            {hasCatalog ? 'Create Session' : 'Open Classes'}
          </Button>
        </div>

        <div className="mt-6 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <CoachSetupActionCard
            done={hasCatalog}
            icon={BookOpenCheck}
            title="Classes"
            value={hasCatalog ? `${programCount} classes` : 'Catalog empty'}
            actionLabel="Open Classes"
            onClick={onOpenCatalog}
          />
          <CoachSetupActionCard
            done={hasAccess}
            icon={KeyRound}
            title="Create Join Code"
            value={activeJoinCodeCount > 0 ? `${activeJoinCodeCount} active` : pendingInvitationCount > 0 ? `${pendingInvitationCount} invites` : 'Family access'}
            actionLabel="Open Access"
            onClick={onOpenAccess}
          />
          <CoachSetupActionCard
            done={hasStudents}
            icon={Users}
            title="Add Students"
            value={hasStudents ? `${linkedAthleteCount} active` : 'Roster empty'}
            actionLabel="Open Students"
            onClick={onOpenDirectory}
          />
          <CoachSetupActionCard
            done={false}
            icon={Calendar}
            title="Create Session"
            value={linkedAthleteCount > 0 ? `${linkedAthleteCount} students` : 'Schedule empty'}
            actionLabel="Open Schedule"
            onClick={onOpenWork}
          />
        </div>
      </CardContent>
    </Card>
  );
}

function CoachSetupActionCard({
  done,
  icon: Icon,
  title,
  value,
  actionLabel,
  onClick,
}: {
  done: boolean;
  icon: ComponentType<{ className?: string }>;
  title: string;
  value: string;
  actionLabel: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex min-h-[150px] flex-col justify-between rounded-lg border border-border bg-surface-elevated/20 p-4 text-left transition-colors hover:border-primary/30 hover:bg-white"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="rounded-lg bg-primary/10 p-2.5 text-primary">
          <Icon className="h-5 w-5" />
        </div>
        {done ? <CheckCircle2 className="h-5 w-5 text-primary" /> : null}
      </div>
      <div>
        <p className="text-sm font-semibold text-text">{title}</p>
        <p className="mt-1 text-xs font-medium text-text-subtle">{value}</p>
        <p className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary">
          {actionLabel}
          <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
        </p>
      </div>
    </button>
  );
}
